import classnames from 'classnames/bind'
import React, { DOMAttributes, ReactNode } from 'react'
import { UrlObject } from 'url'

import Line, { LineProps, LineThemes } from '~/components/Line'
import Link, { LinkProps } from '~/components/Link'

import { useStyle } from '~/providers/StyleProvider'

import useUnderline from '~/hooks/useUnderline'

import LineCenter from '../LineCenter'
import { ReactComponent as Icon } from './icon.svg'
import css from './styles.module.scss'

const cx = classnames.bind(css)

export type InlineCtaTextPresets =
  | 'cta-small'
  | 'cta-label'
  | 'cta-label-small'
  | 'inherit'
export type InlineCtaThemes = LineThemes
export type InlineType = 'inline' | 'bottom_center' | 'left_center'

interface BaseProps extends DOMAttributes<any> {
  className?: string
  lineClassName?: string
  href?: string | UrlObject
  children?: ReactNode
  theme?: InlineCtaThemes
  linkType?: 'Web' | 'Document' | 'Media'
  forceUnderline?: boolean
  withUnderLine?: boolean
  textPreset?: InlineCtaTextPresets
  lineProps?: LineProps
  disabled?: boolean
  icon?: boolean
  ctaType?: 'inline'
  type?: InlineType
}

export type InlineCtaProps = BaseProps | (LinkProps & BaseProps)
/**
 * Inline cta component
 * @param props
 * @returns Component
 */
function InlineCta({
  className,
  children,
  href,
  theme,
  onMouseEnter,
  onMouseLeave,
  forceUnderline,
  withUnderLine,
  textPreset,
  lineProps,
  linkType,
  ctaType,
  icon,
  type,
  ...rest
}: InlineCtaProps) {
  const ctaTextStyle = useStyle({
    textPreset,
    color: theme,
  })

  const isExternal = linkType !== 'Document'

  const classNames = cx(className, css.InlineCta, ctaTextStyle, {
    isBottomCenter: type === 'bottom_center',
    isLeftCenter: type === 'left_center',
  })

  const { shouldUnderline, ...underlineMouseEvents } = useUnderline({
    onMouseEnter,
    onMouseLeave,
    forceUnderline,
  })

  const { className: lineClassName, ...restLineProps } = lineProps
  const innerContent = (
    <span className={css.children}>
      {children}
      {withUnderLine &&
        (type === 'inline' ? (
          <Line
            className={cx(lineClassName, css.line)}
            isVisible={shouldUnderline}
            {...restLineProps}
          />
        ) : (
          <LineCenter
            className={cx(lineClassName, css.line)}
            isVisible={shouldUnderline}
            {...restLineProps}
          />
        ))}
    </span>
  )

  return href ? (
    <Link
      href={href}
      className={cx(classNames, {
        [`theme-${theme}`]: theme,
      })}
      isExternal={isExternal}
      {...rest}
      {...underlineMouseEvents}>
      {innerContent}
      {icon && <Icon className={css.icon} />}
    </Link>
  ) : (
    <button
      className={cx(classNames, {
        [`theme-${theme}`]: theme,
      })}
      {...rest}
      {...underlineMouseEvents}>
      {innerContent}
      {icon && <Icon className={css.icon} />}
    </button>
  )
}

InlineCta.defaultProps = {
  theme: 'black',
  textPreset: 'cta-small',
  color: 'black',
  lineProps: {
    acumulator: 1,
    initialLineState: 'none',
    thickness: 2,
  },
  linkType: 'Document',
  type: 'inline',
  withUnderLine: true,
}

export default InlineCta
